import { FileReader, FileWriter } from '@/platform'
import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import GameMallNewModel from '@/model/MallNewClass/GameMallNewModel'
import MallNewInput from '@/model/MallNewClass/MallNewInput'
import FileName from '@/model/ComControl/FileName'
import {
  findResourceFileNodeByStoredPath,
  resolvePreferredSubfolderRelativePath,
} from '@/features/virtual-resource/fileNamePath'
import { hasSkinResourceMarker } from '@/features/skin-loader/runtime'
import { verifyMallNewBinBytes } from './mallNewBinVerifier'

export const PROJECT_MALL_NEW_FILE_NAME = 'MallNew.bin'
export const PROJECT_MALL_NEW_DISK_PATH = `Data/${PROJECT_MALL_NEW_FILE_NAME}`
export const PROJECT_MALL_NEW_OSS_PATH = PROJECT_MALL_NEW_FILE_NAME

const mallNewSidecarMap = new WeakMap<MainProject, MallNewInput>()
const MALL_UI_RESOURCE_FOLDER = '商城'

type MallFileNameLike = {
  name: string
  type: number
}

type MallGoodsResourceLike = {
  GoodBack?: MallFileNameLike | null
  PreviewBack?: MallFileNameLike | null
  Preview?: MallFileNameLike | null
  BoughtBtn?: MallFileNameLike | null
  NoUpPic?: MallFileNameLike | null
}

type MallDetailPageResourceLike = {
  DetailGraph?: MallFileNameLike | null
  PreviewBackGraph?: MallFileNameLike | null
  IndexImage?: MallFileNameLike | null
}

type MallResourceContainerLike = {
  MallBack?: MallFileNameLike | null
  DecorateBack?: MallFileNameLike | null
  GoodAreaBack?: MallFileNameLike | null
  FlowerPic?: MallFileNameLike | null
  GoodDetailPage?: MallDetailPageResourceLike | null
  GoodsList?: MallGoodsResourceLike[] | null
}

type MallImageOptionResourceLike = {
  NoneImage?: MallFileNameLike | null
  MoveImage?: MallFileNameLike | null
}

function toUint8Array(bytes: ArrayBuffer | Uint8Array): Uint8Array {
  return bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
}

function resolveMallSearch(project: Pick<MainProject, 'screenHash' | 'Width' | 'Height'>): {
  Width: number
  Height: number
} {
  const width = Math.max(1, Number(project.screenHash?.width ?? project.Width ?? 1280))
  const height = Math.max(1, Number(project.screenHash?.height ?? project.Height ?? 720))
  return { Width: width, Height: height }
}

function resolveMallFontColor(project: Pick<MainProject, 'systemData'>): string {
  const value = String(project.systemData?.totalSetting?.FontColor_UI ?? '').trim()
  return value || '255,255,255'
}

function resolveMallDataVersion(project: Pick<MainProject, 'projectConstant' | 'dataVer'>): number {
  return Math.max(1, Number(project.projectConstant?.dataVersion ?? project.dataVer ?? 1))
}

function resolveLegacyPayload(bytes: Uint8Array): Uint8Array | null {
  if (bytes.byteLength <= 4) return null
  const reader = new FileReader(bytes)
  const declaredLength = reader.readInt()
  if (declaredLength !== bytes.byteLength - 4) return null
  return reader.readSome(declaredLength)
}

function resolveMallUiResourcePath(graphicRoot: DataFileMap | null | undefined, value: string): string {
  return resolvePreferredSubfolderRelativePath(graphicRoot, 'graphic', value, '图片/UI', MALL_UI_RESOURCE_FOLDER)
}

function resolveMallButtonResourcePath(graphicRoot: DataFileMap | null | undefined, value: string): string {
  return resolvePreferredSubfolderRelativePath(graphicRoot, 'graphic', value, '图片/button', MALL_UI_RESOURCE_FOLDER)
}

function fixMallFileNamePath(
  graphicRoot: DataFileMap | null | undefined,
  file: MallFileNameLike | null | undefined,
  resolvePath: (graphicRoot: DataFileMap | null | undefined, value: string) => string = resolveMallUiResourcePath,
  baseFolderPath = '图片/UI',
): boolean {
  if (!file) return false
  const currentNode = findResourceFileNodeByStoredPath(graphicRoot, 'graphic', file.name, baseFolderPath)
  if (currentNode && hasSkinResourceMarker(currentNode)) return false
  const nextName = resolvePath(graphicRoot, file.name)
  const nextType = FileName.DEF_TYPE
  if (!nextName || (file.name === nextName && file.type === nextType)) return false
  file.name = nextName
  file.type = nextType
  return true
}

/**
 * 兼容旧 OUI 里商城图片路径不规范的问题。
 *
 * 旧文件经常只写图片文件名或写错目录；如果当前资源树的 `UI/商城` 下存在同名图片，
 * 则把商城数据结构里的路径修正为相对 `UI` 基准目录的 `商城/文件名`。
 *
 * @param mall 商城数据或商城设置草稿。
 * @param graphicRoot 图像虚拟资源树。
 * @returns 是否发生过路径修正。
 */
function normalizeMallNewUiResourceContainerPaths(
  mall: MallResourceContainerLike | null | undefined,
  graphicRoot: DataFileMap | null | undefined,
): boolean {
  if (!mall || !graphicRoot) return false
  let changed = false
  changed = fixMallFileNamePath(graphicRoot, mall.MallBack) || changed
  changed = fixMallFileNamePath(graphicRoot, mall.DecorateBack) || changed
  changed = fixMallFileNamePath(graphicRoot, mall.GoodAreaBack) || changed
  changed = fixMallFileNamePath(graphicRoot, mall.FlowerPic) || changed
  changed = fixMallFileNamePath(graphicRoot, mall.GoodDetailPage?.DetailGraph) || changed
  changed = fixMallFileNamePath(graphicRoot, mall.GoodDetailPage?.PreviewBackGraph) || changed
  changed = fixMallFileNamePath(graphicRoot, mall.GoodDetailPage?.IndexImage) || changed
  for (const goods of mall.GoodsList ?? []) {
    changed = fixMallFileNamePath(graphicRoot, goods.GoodBack) || changed
    changed = fixMallFileNamePath(graphicRoot, goods.PreviewBack) || changed
    changed = fixMallFileNamePath(graphicRoot, goods.Preview) || changed
    changed = fixMallFileNamePath(graphicRoot, goods.BoughtBtn) || changed
    changed = fixMallFileNamePath(graphicRoot, goods.NoUpPic) || changed
  }
  return changed
}

/**
 * 兼容旧 OUI 里图片选项按钮路径不规范的问题。
 * @param buttons 图片选项按钮列表。
 * @param graphicRoot 图像虚拟资源树。
 * @returns 是否发生过路径修正。
 */
function normalizeMallImageOptionResourcePaths(
  buttons: MallImageOptionResourceLike[] | null | undefined,
  graphicRoot: DataFileMap | null | undefined,
): boolean {
  if (!buttons || !graphicRoot) return false
  let changed = false
  for (const button of buttons) {
    changed = fixMallFileNamePath(graphicRoot, button.NoneImage, resolveMallButtonResourcePath, '图片/button') || changed
    changed = fixMallFileNamePath(graphicRoot, button.MoveImage, resolveMallButtonResourcePath, '图片/button') || changed
  }
  return changed
}

/**
 * 校验并修正导入 UI 数据后的工程资源路径。
 * @description 仅在应用 OUI/OUIQ 后执行一次，统一修正工程系统图片选项、商城等数据结构里的旧路径。
 * @param project 当前工程数据结构。
 * @param graphicRoot 图像虚拟资源树。
 * @returns 是否发生过路径修正。
 */
export function validateImportedProjectResourcePaths(
  project: MainProject,
  graphicRoot: DataFileMap | null | undefined,
): boolean {
  const mallChanged = normalizeMallNewUiResourceContainerPaths(project.GameMallNew, graphicRoot)
  const buttonChanged = normalizeMallImageOptionResourcePaths(project.systemData?.btnList?.buttons, graphicRoot)
  return mallChanged || buttonChanged
}

function createMallNewInput(project: MainProject): MallNewInput {
  const input = new MallNewInput(
    resolveMallDataVersion(project),
    resolveMallFontColor(project),
    resolveMallSearch(project),
  )
  input.GameMallNew = new GameMallNewModel(resolveMallFontColor(project), resolveMallSearch(project))
  return input
}

function hasSystemButton(project: MainProject, index: number): boolean {
  const buttons = project.systemData?.btnList?.buttons
  return Array.isArray(buttons)
    && Number.isInteger(index)
    && index >= 0
    && index < buttons.length
    && Boolean(buttons[index])
}

function normalizeMallNewRuntimeButtonRefs(project: MainProject, input: MallNewInput): void {
  const mall = input.GameMallNew
  if (!mall.IsShowOneKeyGetBtn) return
  const index = Number(mall.OneKeyGetBtn?.Index ?? -1)
  if (hasSystemButton(project, index)) return
  mall.IsShowOneKeyGetBtn = false
}

function attachMallNewInput(project: MainProject, input: MallNewInput): MallNewInput {
  mallNewSidecarMap.set(project, input)
  project.GameMallNew = input.GameMallNew
  return input
}

export function getMallNewInput(project: MainProject): MallNewInput | null {
  return mallNewSidecarMap.get(project) ?? null
}

export function createDefaultMallNew(project: MainProject): MallNewInput {
  const input = createMallNewInput(project)
  return attachMallNewInput(project, input)
}

export function ensureMallNewForProject(project: MainProject): MallNewInput {
  const existing = mallNewSidecarMap.get(project)
  if (existing) {
    if (project.GameMallNew && existing.GameMallNew !== project.GameMallNew) {
      existing.GameMallNew = project.GameMallNew
    } else if (!project.GameMallNew) {
      project.GameMallNew = existing.GameMallNew
    }
    return existing
  }

  const input = createMallNewInput(project)
  if (project.GameMallNew) {
    input.GameMallNew = project.GameMallNew
  }
  return attachMallNewInput(project, input)
}

export function readMallNewBin(bytes: ArrayBuffer | Uint8Array, project: MainProject): MallNewInput {
  const input = createMallNewInput(project)
  const mallBytes = toUint8Array(bytes)
  const legacyPayload = resolveLegacyPayload(mallBytes)
  if (legacyPayload) {
    input.readLegacyData(new FileReader(legacyPayload), resolveMallDataVersion(project))
  } else {
    input.GameMallNew = new GameMallNewModel(resolveMallFontColor(project), resolveMallSearch(project))
    input.GameMallNew.readMallBin(new FileReader(mallBytes))
    input.Version = input.GameMallNew.Version
  }
  return attachMallNewInput(project, input)
}

export function setMallNewInput(project: MainProject, input: MallNewInput): MallNewInput {
  return attachMallNewInput(project, input)
}

export function writeMallNewBin(project: MainProject): Uint8Array {
  const input = ensureMallNewForProject(project)
  normalizeMallNewRuntimeButtonRefs(project, input)
  const writer = new FileWriter()
  input.GameMallNew.writeMallBin(writer)
  const bytes = writer.toUint8Array()
  verifyMallNewBinBytes(bytes)
  return bytes
}
